import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

interface LocalProjectSettings {
  workDays: number[];
  hoursPerDay: number;
  excludedDates: string[];
  [key: string]: unknown;
}

interface ProjectSettingsModalProps {
  onClose: () => void;
}

const ProjectSettingsModal = ({ onClose }: ProjectSettingsModalProps) => {
  const t = useCopy().modals.projectSettings;
  const { project, updateProjectSettings } = useProject();
  const [settings, setSettings] = useState<LocalProjectSettings>(() => {
    const s = project?.settings as LocalProjectSettings | undefined;
    return {
      workDays: s?.workDays || [1, 2, 3, 4, 5],
      hoursPerDay: s?.hoursPerDay || 8,
      excludedDates: (s?.excludedDates as string[]) || []
    };
  });
  const [showHolidaysEditor, setShowHolidaysEditor] = useState(false);
  const [holidaysText, setHolidaysText] = useState(
    (settings.excludedDates || []).join(', ')
  );

  // O valor É o índice: 0..6 casa com Date.getDay() e é o que vai para
  // settings.workDays. Só o rótulo vem do dicionário, na mesma ordem.
  const weekDays = t.weekDays.map((label, value) => ({ value, label }));

  const handleWorkDayToggle = (day: number) => {
    const newWorkDays = settings.workDays.includes(day)
      ? settings.workDays.filter(d => d !== day)
      : [...settings.workDays, day].sort((a, b) => a - b);

    setSettings({ ...settings, workDays: newWorkDays });
  };

  /**
   * Espera a gravação antes de fechar.
   *
   * Antes o `updateProjectSettings` era disparado sem `await` e o modal fechava
   * na linha seguinte. Duas consequências: reabrir logo em seguida mostrava o
   * valor ANTIGO (a tela mentia sobre o que estava salvo), e o usuário perdia a
   * única pista de que a operação é pesada, porque salvar o calendário recalcula
   * e regrava a data de TODAS as tarefas do projeto.
   */
  const [salvando, setSalvando] = useState(false);
  const handleSave = async () => {
    setSalvando(true);
    try {
      await updateProjectSettings(settings);
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[600px] p-0 gap-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="py-5 px-6 border-b border-[var(--color-border)]">
          <DialogTitle>{t.title}</DialogTitle>
        </DialogHeader>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-8 last:mb-0">
            <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">{t.workDaysTitle}</h3>
            <p className="text-[13px] text-[var(--color-text-secondary)] mb-4 leading-normal">
              {t.workDaysHint}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {weekDays.map(day => (
                <label key={day.value} className="flex items-center gap-2 p-2.5 border border-[var(--color-border)] rounded-[var(--radius-sm)] cursor-pointer transition-all duration-150 hover:bg-[var(--color-bg-secondary)] hover:border-[var(--color-primary)]">
                  <input
                    type="checkbox"
                    checked={settings.workDays.includes(day.value)}
                    onChange={() => handleWorkDayToggle(day.value)}
                    className="w-[18px] h-[18px] cursor-pointer accent-[var(--color-primary)]"
                  />
                  <span className="text-[13px] text-[var(--color-text)]">{day.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-8 last:mb-0">
            <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">{t.hoursTitle}</h3>
            <div className="mb-4 last:mb-0">
              <Input
                type="number"
                min="1"
                max="24"
                value={settings.hoursPerDay}
                onChange={(e) => setSettings({ ...settings, hoursPerDay: parseInt(e.target.value) })}
                className="w-full py-2 px-3 text-[13px] border border-[var(--color-border)] rounded-[var(--radius-sm)] outline-none transition-[border-color] duration-150 focus:border-[var(--color-primary)]"
              />
            </div>
          </div>

          <div className="mb-8 last:mb-0">
            <h3 className="text-sm font-semibold text-[var(--color-text)] mb-2">{t.holidaysTitle}</h3>
            <p className="text-[13px] text-[var(--color-text-secondary)] mb-4 leading-normal">
              {t.holidaysHint}
            </p>
            {!showHolidaysEditor ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13px] text-[var(--color-text-secondary)]">
                  {fill(plural((settings.excludedDates || []).length, t.daysConfigured), { count: (settings.excludedDates || []).length })}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowHolidaysEditor(true)}
                >
                  <Calendar size={14} />
                  {t.edit}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Textarea
                  className="w-full p-3 text-[13px] font-[inherit] border border-[var(--color-border)] rounded-[var(--radius-sm)] resize-y min-h-[80px] outline-none transition-[border-color] duration-150 focus:border-[var(--color-primary)]"
                  value={holidaysText}
                  onChange={(e) => setHolidaysText(e.target.value)}
                  placeholder={t.holidaysPlaceholder}
                  rows={4}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowHolidaysEditor(false);
                      setHolidaysText((settings.excludedDates || []).join(', '));
                    }}
                  >
                    {t.cancel}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      const dates = holidaysText
                        .split(/[,;\n]+/)
                        .map(d => d.trim())
                        .filter(d => d.length > 0);
                      setSettings({ ...settings, excludedDates: dates });
                      setShowHolidaysEditor(false);
                    }}
                  >
                    {t.apply}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="py-4 px-6 border-t border-[var(--color-border)]">
          <Button variant="secondary" onClick={onClose} disabled={salvando}>
            {t.cancel}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={salvando}>
            {t.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSettingsModal;
